import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { normalizeShopKey } from '../../utils/publishTargets';
import SearchableSelect from '../shared/SearchableSelect';
import {
  groupToolsByFamily,
  inheritOptionLabel,
  resolveThreeState,
  threeStateToOverrideValue,
} from '../shared/mcpServerTools';

// Admin-only "MCP Tools" tab (Admin Dashboard → MCP Tools). Staff control of
// which tools THIS app's inbound MCP SERVER exposes — globally, and per
// store as a pilot override (spec §7.1). This is unrelated to
// `MCPConnectorsPage` (`client/src/mcp-connectors/`), which is this app
// acting as an MCP CLIENT to third-party servers; that page and this one
// share no code and should never be confused for each other.
//
// The rule ("a store override set to on beats a global off") is decided
// entirely server-side by mcpToolPolicy.js#staffToolViewFor — every row's
// `source` ("store" | "global" | "default"), `enabled` and `globalEnabled`
// (what "Inherit" would resolve to) come from ONE read there (see
// shared/mcpServerTools.js#resolveThreeState). This component only displays
// that decision and posts the staff's next pick; it never re-derives
// precedence itself — not even optimistically: while a per-store save is in
// flight the control shows the staffer's pick, and the refetch that follows
// is what the row then shows.
//
// Store identity: `/admin/stores` returns the RAW shop
// (`shopify_store_settings.shop`, e.g. "acme.myshopify.com") while the
// policy — `storesWithOverrides` and a store view's `shop` — uses the
// NORMALIZED key ("acme"). Names are looked up by normalizing both sides with
// the client's existing mirror of the server's normalizeShopKey
// (utils/publishTargets.js), never by comparing the two spellings directly.
//
// Data comes from `/admin/mcp-tools*`
// (server/services/cognitive-flows/mcpAdminToolsRoutes.js). When the
// catalog can't be fetched from the Worker, `catalog`/`tools` come back
// null: names can't be validated, so every save control here is disabled,
// and whatever raw settings did come back (the global off-list, or a
// store's raw overrides) still render — read-only.

const s = {
  embedded: { background: 'var(--surface,#fff)', borderRadius: '14px', width: '100%', maxWidth: '980px', border: '1px solid var(--border)', display: 'flex', flexDirection: 'column', overflow: 'hidden' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 22px', borderBottom: '1px solid var(--border)', flexShrink: 0 },
  title: { fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)' },
  body: { padding: '20px 22px', display: 'flex', flexDirection: 'column', gap: '20px' },
  note: { fontSize: '12px', lineHeight: 1.6, color: 'var(--text-secondary)', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', padding: '12px 14px', margin: 0 },
  errorNote: { fontSize: '12px', lineHeight: 1.6, color: '#991b1b', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '10px', padding: '12px 14px', margin: 0 },
  sectionTitle: { fontWeight: 700, fontSize: '14px', color: 'var(--text-primary)', margin: 0 },
  card: { border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th: { textAlign: 'left', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)', padding: '10px 14px', background: '#fbfcfe', borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap' },
  td: { padding: '11px 14px', borderBottom: '1px solid var(--border)', color: 'var(--text-primary)', verticalAlign: 'middle' },
  groupRow: { padding: '9px 14px', background: '#f8fafc', borderBottom: '1px solid var(--border)', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)' },
  name: { fontWeight: 600 },
  meta: { fontSize: '11px', color: 'var(--text-muted)' },
  empty: { fontSize: '13px', color: 'var(--text-muted)', padding: '18px 14px' },
  toggleBtn: (on, disabled) => ({
    padding: '5px 14px', borderRadius: '8px', fontWeight: 600, fontSize: '12px',
    cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, whiteSpace: 'nowrap',
    border: `1px solid ${on ? 'var(--success, #057a55)' : 'var(--border)'}`,
    background: on ? 'var(--success-light, #dcfce7)' : '#fff',
    color: on ? 'var(--success, #057a55)' : 'var(--text-secondary)',
  }),
  triSelect: (disabled) => ({ padding: '6px 8px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '12px', background: '#fff', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1 }),
  storePicker: { display: 'flex', flexDirection: 'column', gap: '5px', maxWidth: '360px' },
  pickerLabel: { fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' },
  overridesList: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  overridePill: { padding: '5px 12px', borderRadius: '99px', border: '1px solid var(--border)', background: '#fff', fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', cursor: 'pointer' },
  rawList: { margin: '8px 0 0', paddingLeft: '18px', fontSize: '12px', lineHeight: 1.7, color: 'var(--text-secondary)' },
  errorText: { color: '#991b1b' },
  retryBtn: { padding: '4px 12px', borderRadius: '8px', fontWeight: 600, fontSize: '12px', cursor: 'pointer', border: '1px solid #fecaca', background: '#fff', color: '#991b1b' },
};

/** Confirmation copy for turning a tool off globally — spec §7.1. */
const confirmGlobalOff = (tool) =>
  `This turns "${tool.name}" off for every store that has no override of its own.`;

export default function McpServerToolsManager() {
  // —— overview: catalog + global settings + which stores have overrides ——
  const [catalog, setCatalog] = useState(null);
  const [catalogError, setCatalogError] = useState(null);
  const [globalTools, setGlobalTools] = useState({});
  const [storesWithOverrides, setStoresWithOverrides] = useState([]);
  const [overviewLoading, setOverviewLoading] = useState(true);
  const [savingGlobal, setSavingGlobal] = useState({});

  // —— per-store view ——
  const [stores, setStores] = useState([]);
  const [selectedShop, setSelectedShop] = useState('');
  const [storeView, setStoreView] = useState(null); // { shop, overrides, tools, catalogError }
  const [storeViewLoading, setStoreViewLoading] = useState(false);
  // null | 'load' (the store's settings could not be fetched) | 'reload'
  // (a save went through, but the refetch that should show it did not).
  const [storeViewError, setStoreViewError] = useState(null);
  // `${shop}\u0000${toolName}` → the pick being saved, keyed by the SERVER's
  // shop key so a save that settles after the staffer has moved to another
  // store never touches that store's rows.
  const [savingStoreTool, setSavingStoreTool] = useState({});

  // The store the staffer currently has selected, readable synchronously by
  // any response that comes back later — so a late answer for store A is
  // dropped instead of rendering under store B's name (and sending B's later
  // saves to A). `storeRequestRef` numbers every store-view fetch; only the
  // newest may land.
  const selectedShopRef = useRef('');
  const storeRequestRef = useRef(0);

  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      const { data } = await api.get('/admin/mcp-tools');
      setCatalog(Array.isArray(data?.catalog) ? data.catalog : null);
      setCatalogError(data?.catalogError || null);
      setGlobalTools(data?.global || {});
      setStoresWithOverrides(Array.isArray(data?.storesWithOverrides) ? data.storesWithOverrides : []);
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to load MCP tool settings');
    } finally {
      setOverviewLoading(false);
    }
  }, []);

  useEffect(() => { loadOverview(); }, [loadOverview]);

  // After a per-store save, "Stores with an override" may have gained or lost
  // that store. Re-read the server's own list rather than inferring it; only
  // that list is taken from the response, so a global toggle in flight is
  // never overwritten.
  const refreshStoresWithOverrides = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/mcp-tools');
      if (Array.isArray(data?.storesWithOverrides)) setStoresWithOverrides(data.storesWithOverrides);
    } catch (err) {
      toast.error('Saved, but the list of stores with an override could not be refreshed.');
    }
  }, []);

  useEffect(() => {
    api.get('/admin/stores').then(({ data }) => setStores(Array.isArray(data) ? data : [])).catch(() => toast.error('Failed to load stores'));
  }, []);

  const storeOptions = useMemo(
    () => [...stores].sort((a, b) => a.name.localeCompare(b.name)).map((st) => ({ value: st.shopKey, label: st.name, hint: st.shopKey })),
    [stores],
  );
  // Normalized key → the store's row, so the policy's normalized keys and
  // /admin/stores' raw shops meet in one spelling.
  const storeByKey = useMemo(
    () => new Map(stores.map((st) => [normalizeShopKey(st.shopKey), st])),
    [stores],
  );
  const storeName = useCallback(
    (shop) => storeByKey.get(normalizeShopKey(shop))?.name || shop,
    [storeByKey],
  );

  const loadStoreView = useCallback(async (shop, { afterSave = false } = {}) => {
    // A refetch for a store the staffer has since left is not even sent.
    if (!shop || shop !== selectedShopRef.current) return;
    const requestId = ++storeRequestRef.current;
    const isCurrent = () => requestId === storeRequestRef.current && shop === selectedShopRef.current;
    setStoreViewLoading(true);
    try {
      const { data } = await api.get(`/admin/mcp-tools/stores/${encodeURIComponent(shop)}`);
      if (!isCurrent()) return;
      setStoreView(data);
      setStoreViewError(null);
    } catch (err) {
      if (!isCurrent()) return;
      setStoreView(null);
      setStoreViewError(afterSave ? 'reload' : 'load');
      if (!afterSave) toast.error(err?.response?.data?.error || 'Failed to load that store’s MCP tool settings');
    } finally {
      if (requestId === storeRequestRef.current) setStoreViewLoading(false);
    }
  }, []);

  /** Select a store (picker or pill). Clears the previous store's rows at
   * once, so nothing of store A is ever shown — or saved — under store B. */
  const pickStore = useCallback((shop) => {
    const next = shop || '';
    selectedShopRef.current = next;
    setSelectedShop(next);
    setStoreView(null);
    setStoreViewError(null);
    if (!next) {
      storeRequestRef.current += 1; // any fetch still in flight is now stale
      setStoreViewLoading(false);
      return;
    }
    loadStoreView(next);
  }, [loadStoreView]);

  /** A pill carries the policy's normalized key; select the picker's own
   * option for that store when there is one, so the picker shows its name. */
  const pickStoreByKey = (shopKey) => pickStore(storeByKey.get(normalizeShopKey(shopKey))?.shopKey || shopKey);

  const catalogAvailable = Array.isArray(catalog);

  // When the catalog can't be fetched, still show whatever the global
  // settings mention (spec §8: "render whatever else came back") — just
  // with no description/family metadata and every control disabled.
  const globalToolEntries = useMemo(() => {
    if (catalogAvailable) return catalog;
    return Object.keys(globalTools).map((name) => ({ name, description: null, family: null, scopes: [] }));
  }, [catalog, catalogAvailable, globalTools]);
  const globalGroups = useMemo(() => groupToolsByFamily(globalToolEntries), [globalToolEntries]);

  const handleGlobalToggle = async (tool) => {
    const name = tool.name;
    if (savingGlobal[name] || !catalogAvailable) return;
    const isCurrentlyOn = globalTools[name] !== false;
    if (isCurrentlyOn) {
      if (!window.confirm(confirmGlobalOff(tool))) return;
    }
    // Only THIS tool's stored value is remembered for a revert — a snapshot of
    // every tool would, on failure, also undo another tool's save that the
    // server did accept in the meantime.
    const hadStoredValue = Object.prototype.hasOwnProperty.call(globalTools, name);
    const prevStoredValue = globalTools[name];
    const nextValueForServer = !isCurrentlyOn; // true unsets (back to on); false sets off
    setGlobalTools((g) => {
      if (isCurrentlyOn) return { ...g, [name]: false };
      const next = { ...g };
      delete next[name];
      return next;
    });
    setSavingGlobal((m) => ({ ...m, [name]: true }));
    try {
      const { data } = await api.put('/admin/mcp-tools/global', { tools: { [name]: nextValueForServer } });
      setGlobalTools(data?.global || {});
    } catch (err) {
      setGlobalTools((g) => {
        const next = { ...g };
        if (hadStoredValue) next[name] = prevStoredValue;
        else delete next[name];
        return next;
      });
      toast.error(err?.response?.data?.error || 'Failed to save.');
    } finally {
      setSavingGlobal((m) => { const next = { ...m }; delete next[name]; return next; });
    }
  };

  const storeCatalogAvailable = Array.isArray(storeView?.tools);
  const storeToolEntries = useMemo(() => {
    if (!storeView) return [];
    if (storeCatalogAvailable) return storeView.tools;
    // Catalog unavailable for this fetch — fall back to the raw overrides so
    // staff can still see what's set, with saving disabled (spec §8).
    return Object.entries(storeView.overrides || {}).map(([name, value]) => ({
      name, description: null, family: null, enabled: value !== false, source: 'store',
    }));
  }, [storeView, storeCatalogAvailable]);
  const storeGroups = useMemo(() => groupToolsByFamily(storeToolEntries), [storeToolEntries]);

  const savingKey = (shop, name) => `${shop}\u0000${name}`;

  const handleStoreStateChange = async (tool, nextState) => {
    if (!storeView || !storeCatalogAvailable) return;
    const name = tool.name;
    // The server's own key for the store whose rows these are (never the
    // picker's value, which may be the raw shop).
    const shop = storeView.shop;
    const key = savingKey(shop, name);
    if (savingStoreTool[key] !== undefined) return;
    // The picker value this view was loaded for — the refetch below only
    // runs if the staffer is still on it.
    const selectedAtSave = selectedShopRef.current;
    const overrideValue = threeStateToOverrideValue(nextState);
    setSavingStoreTool((m) => ({ ...m, [key]: nextState }));
    try {
      await api.put(`/admin/mcp-tools/stores/${encodeURIComponent(shop)}`, { tools: { [name]: overrideValue } });
    } catch (err) {
      // Nothing was saved: dropping the pending pick is the whole revert —
      // the row shows the server's last answer again.
      setSavingStoreTool((m) => { const next = { ...m }; delete next[key]; return next; });
      toast.error(err?.response?.data?.error || 'Failed to save.');
      return;
    }
    // Saved. Show what the server now says (loadStoreView drops the answer if
    // the staffer has moved on) and refresh the override list alongside.
    await Promise.all([
      loadStoreView(selectedAtSave, { afterSave: true }),
      refreshStoresWithOverrides(),
    ]);
    setSavingStoreTool((m) => { const next = { ...m }; delete next[key]; return next; });
  };

  const renderGlobalRow = (tool) => {
    const isOn = globalTools[tool.name] !== false;
    const busy = Boolean(savingGlobal[tool.name]);
    const disabled = !catalogAvailable || busy;
    return (
      <tr key={tool.name}>
        <td style={s.td}>
          <div style={s.name}>{tool.name}</div>
        </td>
        <td style={{ ...s.td, ...s.meta }}>{tool.description || '—'}</td>
        <td style={{ ...s.td, ...s.meta }}>{Array.isArray(tool.scopes) && tool.scopes.length ? tool.scopes.join(', ') : '—'}</td>
        <td style={{ ...s.td, textAlign: 'right' }}>
          <button
            type="button"
            style={s.toggleBtn(isOn, disabled)}
            disabled={disabled}
            onClick={() => handleGlobalToggle(tool)}
          >
            {busy ? 'Saving…' : isOn ? 'On' : 'Off'}
          </button>
        </td>
      </tr>
    );
  };

  const renderStoreRow = (tool) => {
    const { state, inheritedEnabled } = resolveThreeState(tool);
    const pendingPick = storeView ? savingStoreTool[savingKey(storeView.shop, tool.name)] : undefined;
    const busy = pendingPick !== undefined;
    const disabled = !storeCatalogAvailable || busy;
    return (
      <tr key={tool.name}>
        <td style={s.td}>
          <div style={s.name}>{tool.name}</div>
        </td>
        <td style={{ ...s.td, ...s.meta }}>{tool.description || '—'}</td>
        <td style={{ ...s.td, textAlign: 'right' }}>
          <select
            aria-label={`${tool.name} setting for ${storeName(storeView?.shop)}`}
            style={s.triSelect(disabled)}
            disabled={disabled}
            value={busy ? pendingPick : state}
            onChange={(e) => handleStoreStateChange(tool, e.target.value)}
          >
            <option value="inherit">{inheritOptionLabel(inheritedEnabled)}</option>
            <option value="on">On</option>
            <option value="off">Off</option>
          </select>
        </td>
      </tr>
    );
  };

  return (
    <div style={s.embedded}>
      <div style={s.header}>
        <span style={s.title}>MCP Tools</span>
      </div>
      <div style={s.body}>
        <p style={s.note}>
          Controls which tools the MCP server offers to Claude and ChatGPT connections — globally, and as a
          per-store pilot override. A store override set to On beats a global Off; a store override always
          replaces the global setting for that tool, in either direction. This is separate from the outbound
          MCP Connectors feature.
        </p>

        {catalogError && (
          <p style={s.errorNote}>
            Could not reach the MCP server to fetch its tool catalog. Tool names can&apos;t be validated, so
            saving is disabled below — whatever settings are already stored still show.
          </p>
        )}

        <h3 style={s.sectionTitle}>Global</h3>
        <div style={s.card}>
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>Tool</th>
                <th style={s.th}>Description</th>
                <th style={s.th}>Scopes</th>
                <th style={{ ...s.th, textAlign: 'right' }}>Enabled</th>
              </tr>
            </thead>
            <tbody>
              {overviewLoading ? (
                <tr><td style={s.empty} colSpan={4}>Loading tools…</td></tr>
              ) : globalGroups.length === 0 ? (
                <tr><td style={s.empty} colSpan={4}>No tools to show.</td></tr>
              ) : globalGroups.map((group) => (
                <React.Fragment key={group.family}>
                  <tr><td style={s.groupRow} colSpan={4}>{group.label}</td></tr>
                  {group.tools.map(renderGlobalRow)}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        <h3 style={s.sectionTitle}>Stores with an override</h3>
        {storesWithOverrides.length === 0 ? (
          <p style={s.meta}>No store currently overrides a tool.</p>
        ) : (
          <div style={s.overridesList} role="group" aria-label="Stores with an override">
            {storesWithOverrides.map((shop) => (
              <button
                key={shop}
                type="button"
                style={s.overridePill}
                onClick={() => pickStoreByKey(shop)}
                title={shop}
              >
                {storeName(shop)}
              </button>
            ))}
          </div>
        )}

        <h3 style={s.sectionTitle}>Per store</h3>
        <div style={s.storePicker}>
          <span style={s.pickerLabel}>Store</span>
          <SearchableSelect
            value={selectedShop}
            options={storeOptions}
            onChange={pickStore}
            placeholder="— Select a store —"
          />
        </div>

        {selectedShop && (
          storeView?.catalogError && (
            <p style={s.errorNote}>
              Could not reach the MCP server to fetch its tool catalog for this store. Tool names can&apos;t be
              validated, so saving is disabled below — the store&apos;s raw overrides still show.
            </p>
          )
        )}

        {selectedShop && (
          <div style={s.card}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Tool</th>
                  <th style={s.th}>Description</th>
                  <th style={{ ...s.th, textAlign: 'right' }}>Setting</th>
                </tr>
              </thead>
              <tbody>
                {storeViewLoading && storeGroups.length === 0 ? (
                  // Only blanks the table on the FIRST fetch for this store.
                  // A background refetch after a save (loadStoreView) keeps
                  // showing the rows already on screen instead of unmounting
                  // every control while it reloads.
                  <tr><td style={s.empty} colSpan={3}>Loading…</td></tr>
                ) : storeViewError ? (
                  // A failed fetch is an error, never "No tools to show" — an
                  // empty table would read as "this store has no tools".
                  <tr>
                    <td style={s.empty} colSpan={3}>
                      <span style={s.errorText}>
                        {storeViewError === 'reload'
                          ? 'Your change was saved, but this store’s settings could not be reloaded.'
                          : 'Could not load this store’s MCP tool settings.'}
                      </span>{' '}
                      <button type="button" style={s.retryBtn} onClick={() => loadStoreView(selectedShopRef.current)}>
                        Try again
                      </button>
                    </td>
                  </tr>
                ) : storeGroups.length === 0 ? (
                  <tr><td style={s.empty} colSpan={3}>No tools to show.</td></tr>
                ) : storeGroups.map((group) => (
                  <React.Fragment key={group.family}>
                    <tr><td style={s.groupRow} colSpan={3}>{group.label}</td></tr>
                    {group.tools.map(renderStoreRow)}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
